import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { homePathForContext, useSession } from './auth/session.js'
import { ErrorBoundary } from './lib/ErrorBoundary.js'
import { AccountNotificationsPage } from './pages/account/AccountNotifications.js'
import { AccountProfilePage } from './pages/account/AccountProfile.js'
import { AccountSecurityPage } from './pages/AccountSecurity.js'
import { AccountManagersPage } from './pages/admin/AccountManagers.js'
import { BotConfigPage } from './pages/admin/BotConfig.js'
import { CfdiQueuePage } from './pages/admin/CfdiQueue.js'
import { AdminConciergePage } from './pages/admin/Concierge.js'
import { ConversationDebugPage } from './pages/admin/ConversationDebug.js'
import { AdminFlowEditorScreen, OwnerFlowEditorScreen } from './pages/admin/FlowEditorScreen.js'
import { HandoffsPage } from './pages/admin/Handoffs.js'
import { NewTenantPage } from './pages/admin/NewTenant.js'
import { OverviewPage } from './pages/admin/Overview.js'
import { SubscriptionsPage } from './pages/admin/Subscriptions.js'
import { TemplateDetailPage } from './pages/admin/TemplateDetail.js'
import { TemplatesPage } from './pages/admin/Templates.js'
import { TenantDetailPage } from './pages/admin/TenantDetail.js'
import { TenantsPage } from './pages/admin/Tenants.js'
import { UserDetailPage } from './pages/admin/UserDetail.js'
import { UsersPage } from './pages/admin/Users.js'
import { AgendaPage } from './pages/agenda/Agenda.js'
import { ConfirmEmailPage } from './pages/auth/ConfirmEmail.js'
import { ForcePasswordChangePage } from './pages/auth/ForcePasswordChange.js'
import { InviteAcceptPage } from './pages/auth/InviteAccept.js'
import { ResetPasswordPage } from './pages/auth/ResetPassword.js'
import { CatalogPage } from './pages/catalog/Catalog.js'
import { ConciergePage } from './pages/concierge/Concierge.js'
import { ContactDetailPage } from './pages/contacts/ContactDetail.js'
import { ContactsPage } from './pages/contacts/Contacts.js'
import { HomePage } from './pages/Home.js'
import { InboxPage } from './pages/inbox/Inbox.js'
import { LeadDetailPage } from './pages/leads/LeadDetail.js'
import { LeadsPage } from './pages/leads/Leads.js'
import { LoginPage } from './pages/Login.js'
import { MfaPage } from './pages/Mfa.js'
import { NotificationsPage } from './pages/notifications/Notifications.js'
import { OnboardingPage } from './pages/onboarding/Onboarding.js'
import { OversightPage } from './pages/oversight/Oversight.js'
import { ReportDetailPage } from './pages/reports/ReportDetail.js'
import { ReportsPage } from './pages/reports/Reports.js'
import { SettingsPage } from './pages/settings/Settings.js'
import { AppLayout } from './pages/shell/AppLayout.js'
import { LegacyRedirect } from './pages/shell/LegacyRedirect.js'
import { RequireManage } from './pages/shell/RequireManage.js'
import { AgentKnowledgePage, AgentSettingsPage } from './pages/workers/AgentSettingsPage.js'
import { WorkerEditorPage } from './pages/workers/WorkerEditor.js'

// Dev-only living gallery of the design system (lazy → not in the prod bundle).
const DesignSystemShowcase = lazy(() => import('./pages/DesignSystemShowcase.js'))

// Role-aware landing: operators → the console overview, clients → their
// workspace home. Sending an operator to the tenant `/home` 404s (it's
// tenant-scoped), so `/` and unknown paths must resolve by context.
function HomeRedirect(): JSX.Element {
  const session = useSession()
  if (session === null) return <Navigate to="/login" replace />
  return <Navigate to={homePathForContext(session.context)} replace />
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      {/* Top-level backstop: catches render throws in the auth/onboarding routes
          that live outside AppLayout (the shell has its own page-level boundary). */}
      <ErrorBoundary>
        <Routes>
          {/* Auth (no shell) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/mfa" element={<MfaPage />} />
          <Route path="/crear-contrasena" element={<ForcePasswordChangePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/confirmar-correo/:token" element={<ConfirmEmailPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />

          {/* First-run onboarding — focused surface, outside the shell */}
          <Route path="/onboarding" element={<OnboardingPage />} />

          {/* Authenticated app — AppLayout guards + renders the shell around each page. */}
          <Route element={<AppLayout />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/account/profile" element={<AccountProfilePage />} />
            <Route path="/account/security" element={<AccountSecurityPage />} />
            <Route path="/account/notifications" element={<AccountNotificationsPage />} />
            <Route path="/notificaciones" element={<NotificationsPage />} />
            {/* Tenant workspace (pages land here as they're built) */}
            {/* C6 (Decision 3): the product speaks MX Spanish; its URLs now do
                too. The English slugs live on as permanent redirects below —
                every old bookmark, notification link and support instruction
                keeps working. */}
            <Route path="/bandeja" element={<InboxPage />} />
            <Route path="/prospectos" element={<LeadsPage />} />
            <Route path="/prospectos/:id" element={<LeadDetailPage />} />
            <Route path="/contactos" element={<ContactsPage />} />
            <Route path="/contactos/:id" element={<ContactDetailPage />} />
            {/* Permanent English→Spanish redirects. Search params are
                preserved (the ?c= deep links live in notifications and old
                chats); :id paths map one-to-one. */}
            <Route path="/inbox" element={<LegacyRedirect to="/bandeja" />} />
            <Route path="/leads" element={<LegacyRedirect to="/prospectos" />} />
            <Route path="/leads/:id" element={<LegacyRedirect to="/prospectos" withId />} />
            <Route path="/contacts" element={<LegacyRedirect to="/contactos" />} />
            <Route path="/contacts/:id" element={<LegacyRedirect to="/contactos" withId />} />
            <Route path="/reports" element={<LegacyRedirect to="/reportes" />} />
            <Route path="/reports/:id" element={<LegacyRedirect to="/reportes" withId />} />
            <Route path="/catalog" element={<LegacyRedirect to="/catalogo" />} />
            <Route path="/settings" element={<LegacyRedirect to="/configuracion" />} />
            {/* Owner/admin-only tenant surfaces (agents/viewers are redirected) */}
            {/* C3: Agenda is readable by every role — its read-only mode was
                fully built and then routed away from the very people (Asesores)
                who need to SEE the day's citas. Writes stay API-gated. */}
            <Route path="/agenda" element={<AgendaPage />} />
            <Route element={<RequireManage />}>
              {/* Reportes joins the manage-only block: GET /reports and
                  /reports/:id are gated on `reports.read` (owner/admin), so a
                  deep-linked Asesor would otherwise land on a page that can
                  only render an error. Mirrors /supervision. */}
              {/* Soporte joins it for the same reason (PR5): the concierge
                  thread with the account manager is owner/admin work — every
                  /concierge endpoint requires `tenant.concierge` — so a
                  deep-linked Asesor would land on a page that can only 403. */}
              <Route path="/mensajes" element={<ConciergePage />} />
              <Route path="/reportes" element={<ReportsPage />} />
              <Route path="/reportes/:id" element={<ReportDetailPage />} />
              <Route path="/asistente" element={<WorkerEditorPage />} />
              <Route path="/asistente/:botId/flujos" element={<OwnerFlowEditorScreen />} />
              <Route path="/asistente/:botId/ajustes" element={<AgentSettingsPage />} />
              <Route path="/asistente/:botId/conocimiento" element={<AgentKnowledgePage />} />
              <Route path="/supervision" element={<OversightPage />} />
              <Route path="/catalogo" element={<CatalogPage />} />
              <Route path="/configuracion" element={<SettingsPage />} />
            </Route>
            {/* Operator console */}
            <Route path="/admin" element={<OverviewPage />} />
            <Route path="/admin/mensajes" element={<AdminConciergePage />} />
            <Route path="/admin/account-managers" element={<AccountManagersPage />} />
            <Route path="/admin/usuarios" element={<UsersPage />} />
            <Route path="/admin/usuarios/:id" element={<UserDetailPage />} />
            <Route path="/admin/conversations/:id" element={<ConversationDebugPage />} />
            <Route path="/admin/handoffs" element={<HandoffsPage />} />
            <Route path="/admin/tenants" element={<TenantsPage />} />
            <Route path="/admin/tenants/new" element={<NewTenantPage />} />
            <Route path="/admin/tenants/:id" element={<TenantDetailPage />} />
            <Route path="/admin/tenants/:id/bots/:botId" element={<BotConfigPage />} />
            <Route
              path="/admin/tenants/:id/bots/:botId/flows"
              element={<AdminFlowEditorScreen />}
            />
            <Route path="/admin/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/admin/cfdi" element={<CfdiQueuePage />} />
            <Route path="/admin/templates" element={<TemplatesPage />} />
            <Route path="/admin/templates/:id" element={<TemplateDetailPage />} />
          </Route>

          {import.meta.env.DEV && (
            <Route
              path="/dev/design-system"
              element={
                <Suspense fallback={null}>
                  <DesignSystemShowcase />
                </Suspense>
              }
            />
          )}

          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
